import { motion, useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

import {
  DimmedProvider,
  ObjectGapProvider,
  ObjectProvider,
  SectionsProvider,
  useInDimmedObject,
  useInObject,
  useObjectGap,
} from '../internals/objectContext';
import {
  DIVIDED_CLASS,
  DIVIDED_GAP,
  GAP,
  insetClass,
  OBJECT_INSET_CLASS,
  sizingClass,
  UNDER_TITLE_PULL,
} from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';
import './Column.css';

// A bordered section, its hairlines drawn by any Divider a template puts between
// its children
const BOX_CLASS = 'rounded-lg border border-border p-[calc(12*var(--ac-u))]';

// The object sits on white in light because --surface-default is a warm off-white there
const OBJECT_CLASS = `border border-border-subtle bg-white dark:bg-surface shadow-[0_1px_2px_rgba(0,0,0,.04)] ${OBJECT_INSET_CLASS}`;

// Header rows sit 8 units top and bottom and the last section, the body, 12; a child
// marked `data-sections` pads its own rows the same way
const FLUSH_CLASS =
  'p-0 [&>*:not([data-sections])]:px-[var(--ac-flush-px)] [&>*:not([data-sections])]:py-[var(--ac-flush-py)] [&>*:last-child:not([data-sections])]:py-[calc(12*var(--ac-u))]';

export const Column: ComponentFn<typeof catalog, 'Column'> = ({
  props,
  children,
}) => {
  const reducedMotion = useReducedMotion();
  const inObject = useInObject() || props.appearance === 'object';
  const inDimmed = useInDimmedObject() || props.dimmed === true;
  const objectGap = useObjectGap();
  const gap = props.gap ?? 'sm';
  const flush = props.flush === true;
  const divided = props.divided === true;
  const underTitle =
    props.inset === 'taskTitle' && objectGap !== undefined
      ? UNDER_TITLE_PULL[objectGap]
      : undefined;
  return (
    <motion.div
      data-card-object={props.appearance === 'object' || undefined}
      data-divided={divided ? (flush ? 'flush' : 'spaced') : undefined}
      layout
      transition={{ duration: reducedMotion ? 0 : 0.18 }}
      className={cn(
        'flex flex-col min-w-0',
        !flush && !divided && GAP[gap],
        sizingClass(props.sizing),
        insetClass(props.inset),
        underTitle,
        props.appearance === 'box' && BOX_CLASS,
        props.appearance === 'object' && OBJECT_CLASS,
        flush && FLUSH_CLASS,
        divided && DIVIDED_CLASS,
        divided && !flush && DIVIDED_GAP[gap],
        props.dimmed && 'opacity-[0.55]'
      )}
    >
      <ObjectProvider value={inObject}>
        <ObjectGapProvider
          value={props.appearance === 'object' && !flush ? gap : undefined}
        >
          <DimmedProvider value={inDimmed}>
            <SectionsProvider value={{ divided, flush }}>
              {children}
            </SectionsProvider>
          </DimmedProvider>
        </ObjectGapProvider>
      </ObjectProvider>
    </motion.div>
  );
};
